import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import BEMHelper from '@ynap/bem-helper';
import { StyledCarousel } from '@ynap/carousel';
import { useAppState } from '@ynap/immutables-provider';
import { withOptimizely } from '@ynap/optimizely-utils';
import RecommendationsProduct from './RecommendationsProduct';
import carouselConfig from './recommendationsCarouselHelper';
import { upsellListImpression } from '../analytics/';
const bem = new BEMHelper('RecommendationsProducts');

const RecommendationsProductList = ({
    recommendations,
    messages,
    heading,
    className,
    isFullWidth,
    onClick = () => {},
    isCarouselVisible,
    type,
    tracking,
    carouselConfig: carouselConfigOverride,
    addToCart,
    isWithAddToBag = false,
    useDefaultCarouselIndex,
    withQuickAdd = false,
    optimizely,
    variation,
    carouselButtons,
    noDescription,
    isWishlistRecommendations,
    overlayProductId,
    handleOverlayProductIdChange,
    setIsPhoneOverlay,
    CTAButtonsState,
    doubleImageWithoutPadding,
    doubleImageModifier,
    reducedMode = false,
    viewSimilarAndShopMore = null,
    onRemove,
}) => {
    const [lastAddedIndex, setLastAddedIndex] = useState(-1);
    const { addToCart: addToBagState = {} } = useAppState();
    const { loading: addToCartLoading } = addToBagState;
    const recommendationsVisible = recommendations && recommendations?.length > 0;
    const modifiers = [
        recommendationsVisible ? 'showRecommendations' : 'hideRecommendations',
        isFullWidth ? 'fullWidth' : '',
        withQuickAdd ? 'quickAdd' : '',
        reducedMode ? 'reducedMode' : '',
    ];

    const carouselClassNames = {
        prev: carouselButtons?.prev ?? bem('controls', 'arrow-prev'),
        next: carouselButtons?.next ?? bem('controls', 'arrow-next'),
        container: 'overlay',
        trackModifier: 'ymal',
        slideModifier: 'ymal',
    };

    useEffect(() => {
        if (recommendationsVisible) {
            tracking?.trackEvent?.(upsellListImpression(recommendations, type));
        }
    }, [type, recommendationsVisible]);

    useEffect(() => {
        if (!addToCartLoading && lastAddedIndex > -1) {
            const clearLastAdded = setTimeout(() => setLastAddedIndex(-1), 2000);
            return () => clearLastAdded && clearTimeout(clearLastAdded);
        }
    }, [addToCartLoading]);

    return (
        <div className={bem(null, modifiers, className)}>
            {recommendationsVisible && (
                <>
                    <div className={bem('topDivider', reducedMode ? 'reducedMode' : '')} />
                    <section className={bem('sectionWrapper', reducedMode && 'reducedMode')}>
                        {heading && <h3 className={bem('header', modifiers)}>{heading()}</h3>}
                        {isCarouselVisible ? (
                            <StyledCarousel
                                slidesLength={recommendations.length}
                                carouselConfig={carouselConfig(carouselConfigOverride)}
                                carouselClassNames={carouselClassNames}
                                useDefaultCarouselIndex={useDefaultCarouselIndex}
                                onNextClick={() => {
                                    /* istanbul ignore next */
                                    optimizely?.track('recommendations_carousel_control_click');
                                }}
                                onPrevClick={() => {
                                    /* istanbul ignore next */
                                    optimizely?.track('recommendations_carousel_control_click');
                                }}
                            >
                                {recommendations.map((recommendedProduct, index) => {
                                    return (
                                        <RecommendationsProduct
                                            recommendedProduct={recommendedProduct}
                                            index={index}
                                            messages={messages}
                                            onClick={onClick}
                                            key={recommendedProduct && recommendedProduct.href}
                                            modifier={noDescription ? 'descriptionStyle' : 'pdpRecommendations'}
                                            addToCart={(partNumber, { quantity }) => {
                                                setLastAddedIndex(index);
                                                return addToCart(partNumber, { quantity });
                                            }}
                                            tracking={tracking}
                                            isWithAddToBag={isWithAddToBag}
                                            withQuickAdd={withQuickAdd}
                                            isLastAdded={index === lastAddedIndex}
                                            inProgress={lastAddedIndex > -1}
                                            variation={variation}
                                            noDescription={noDescription}
                                            isWishlistRecommendations={isWishlistRecommendations}
                                            overlayProductId={overlayProductId}
                                            handleOverlayProductIdChange={handleOverlayProductIdChange}
                                            setIsPhoneOverlay={setIsPhoneOverlay}
                                            CTAButtonsState={CTAButtonsState}
                                            doubleImageWithoutPadding={doubleImageWithoutPadding}
                                            doubleImageModifier={doubleImageModifier}
                                            type={type}
                                            details={true}
                                            omnibusAlignment={true}
                                            onRemove={onRemove}
                                        />
                                    );
                                })}
                            </StyledCarousel>
                        ) : (
                            <>
                                <div className={bem('products')}>
                                    {recommendations.map((recommendedProduct, index) => {
                                        return (
                                            <RecommendationsProduct
                                                recommendedProduct={recommendedProduct}
                                                index={index}
                                                messages={messages}
                                                onClick={onClick}
                                                key={recommendedProduct && recommendedProduct.href}
                                                modifier={noDescription ? 'descriptionStyle' : 'pdpRecommendations'}
                                                addToCart={(partNumber, { quantity }) => {
                                                    setLastAddedIndex(index);
                                                    return addToCart(partNumber, { quantity });
                                                }}
                                                tracking={tracking}
                                                isWithAddToBag={isWithAddToBag}
                                                withQuickAdd={withQuickAdd}
                                                isLastAdded={index === lastAddedIndex}
                                                inProgress={lastAddedIndex > -1}
                                                variation={variation}
                                                noDescription={noDescription}
                                                reducedMode={reducedMode}
                                                type={type}
                                                onRemove={onRemove}
                                            />
                                        );
                                    })}
                                </div>
                                {viewSimilarAndShopMore}
                            </>
                        )}
                    </section>
                </>
            )}
        </div>
    );
};

RecommendationsProductList.propTypes = {
    recommendations: PropTypes.array,
    messages: PropTypes.shape({
        wearItWith: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
        youMayAlsoLike: PropTypes.shape({
            heading: PropTypes.func.isRequired,
        }).isRequired,
    }),
    heading: PropTypes.string,
    className: PropTypes.string,
    type: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    isCarouselVisible: PropTypes.bool,
    isWithAddToBag: PropTypes.bool,
    addToBagState: PropTypes.object,
    addToCart: PropTypes.func,
    useDefaultCarouselIndex: PropTypes.bool,
    carouselConfig: PropTypes.object,
    tracking: PropTypes.func.isRequired,
    isFullWidth: PropTypes.bool,
    withQuickAdd: PropTypes.bool,
    variation: PropTypes.string,
    carouselButtons: PropTypes.shape({
        prev: PropTypes.any,
        next: PropTypes.any,
    }),
    optimizely: PropTypes.shape({
        track: PropTypes.func.isRequired,
    }),
    isWishlistRecommendations: PropTypes.bool,
    overlayProductId: PropTypes.string,
    handleOverlayProductIdChange: PropTypes.func,
    setIsPhoneOverlay: PropTypes.func,
    doubleImageWithoutPadding: PropTypes.string,
    doubleImageModifier: PropTypes.string,
    intelistyleWiw: PropTypes.array,
    reducedMode: PropTypes.bool,
    viewSimilarAndShopMore: PropTypes.element,
    details: PropTypes.bool,
    omnibusAlignment: PropTypes.bool,
};

RecommendationsProductList.defaultProps = {
    recommendations: [],
    messages: {},
    heading: '',
    className: '',
    isCarouselVisible: false,
    useDefaultCarouselIndex: true,
};

export default withOptimizely(RecommendationsProductList);
